<template>
  <div class="row">
    <spinner-element :loading="loading" />

    <div class="row" v-if="locations.length">
      <div class="col-12" id="details">
        <div class="row" id="road-info" v-if="!loading">
          <div
            v-for="location in locations"
            :key="location.id"
            class="content"
          >
            <div class="d-flex justify-content-between align-items-center mb-2 table-container">
              <h5>
                {{ location.name }}
                <span class="location-meta">({{ location.shortName }}, {{ location.roadNumber }})</span>
              </h5>
              <button @click="toggleLocation(location.id)" class="btn-download">
                {{ expandedLocations[location.id] ? t('collapse') : t('expand') }}
              </button>
            </div>

            <div v-if="expandedLocations[location.id]">
              <div v-if="loadingByLocation[location.id]" class="status-line">
                {{ t('loading') }}
              </div>

              <div v-else-if="errorsByLocation[location.id]" class="status-line error">
                {{ errorsByLocation[location.id] }}
              </div>

              <div
                v-else-if="!statusByLocation[location.id] && !(historyByLocation[location.id] || []).length"
                class="status-line"
              >
                {{ t('no-data-from-location') }}
              </div>

              <div v-else>
                <h6 class="status-section-title">Najnowszy wpis</h6>
                <table v-if="statusByLocation[location.id]" class="center-aligned table table-bordered">
                  <tbody>
                    <tr>
                      <td>
                        <table class="center-aligned table table-striped">
                          <thead>
                            <tr>
                              <th class="field-col">Pole</th>
                              <th class="value-col">Wartość</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="row in toRows(statusByLocation[location.id])" :key="row.label">
                              <td>{{ row.label }}</td>
                              <td>{{ row.value }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="status-line">Brak najnowszego wpisu.</div>

                <h6 class="status-section-title">Historia statusów</h6>
                <table
                  v-if="(historyByLocation[location.id] || []).length"
                  class="center-aligned table table-bordered history-table"
                >
                  <thead>
                    <tr>
                      <th class="lp-col">Lp.</th>
                      <th>Data zapisu</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(entry, index) in historyByLocation[location.id]" :key="entry.id || index">
                      <td>{{ index + 1 }}</td>
                      <td>{{ formatDateTime(entry.createdAt) }}</td>
                      <td>
                        <span :class="['status-chip', getAvailabilityClass(entry)]">
                          {{ getAvailabilityLabel(entry) }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="status-line">Brak historii statusów.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row vehicle-panel" v-if="!loading && !locations.length">
      <div class="col-3" id="no-data-info">
        <div class="row details-row-field">
          {{ t('no-data-for-selected-parameters') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SpinnerElement from "./SpinnerElement.vue";
import LocationStatusesService from "@/services/LocationStatuses";

require("../assets/css/viver-style.css");

export default {
  name: "LocationStatuses",
  components: {
    SpinnerElement,
  },
  data() {
    return {
      loading: false,
      locations: [],
      expandedLocations: {},
      loadingByLocation: {},
      statusByLocation: {},
      historyByLocation: {},
      errorsByLocation: {},
    };
  },
  methods: {
    isNotFoundError(error) {
      return String(error ?? "").includes("404");
    },

    normalizeHistoryResponse(response) {
      if (Array.isArray(response)) {
        return response;
      }
      if (response && Array.isArray(response.items)) {
        return response.items;
      }
      if (response && Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    },

    async loadLocations() {
      this.loading = true;
      try {
        this.locations = await LocationStatusesService.getLocations();
      } catch (error) {
        console.error("Błąd podczas pobierania lokalizacji:", error);
        this.locations = [];
      } finally {
        this.loading = false;
      }
    },
    async toggleLocation(locationId) {
      this.expandedLocations[locationId] = !this.expandedLocations[locationId];

      if (!this.expandedLocations[locationId]) {
        return;
      }

      if (Object.prototype.hasOwnProperty.call(this.statusByLocation, locationId)) {
        return;
      }

      await this.loadStatusForLocation(locationId);
    },
    async loadStatusForLocation(locationId) {
      this.loadingByLocation[locationId] = true;
      this.errorsByLocation[locationId] = "";

      try {
        const [statusResult, historyResult] = await Promise.allSettled([
          LocationStatusesService.getLatestByLocation(locationId),
          LocationStatusesService.getHistoryByLocation(locationId, {
            page: 1,
            limit: 100,
            orderDirection: "desc",
          }),
        ]);

        if (statusResult.status === "fulfilled") {
          this.statusByLocation[locationId] = statusResult.value || null;
        } else if (this.isNotFoundError(statusResult.reason)) {
          this.statusByLocation[locationId] = null;
        } else {
          throw statusResult.reason;
        }

        if (historyResult.status === "fulfilled") {
          this.historyByLocation[locationId] = this.normalizeHistoryResponse(historyResult.value);
        } else if (this.isNotFoundError(historyResult.reason)) {
          this.historyByLocation[locationId] = [];
        } else {
          throw historyResult.reason;
        }
      } catch (error) {
        this.errorsByLocation[locationId] = "Nie udało się pobrać statusu lokalizacji.";
      } finally {
        this.loadingByLocation[locationId] = false;
      }
    },
    formatDateTime(value) {
      if (!value) {
        return "-";
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return String(value);
      }

      return date.toLocaleString();
    },
    formatValue(value) {
      return value === null || value === undefined || value === "" ? "-" : value;
    },
    normalizeStatusValue(value) {
      const numberValue = Number(value);
      return Number.isNaN(numberValue) ? null : numberValue;
    },
    getAvailabilityState(status) {
      if (!status || typeof status !== "object") {
        return "no-data";
      }

      const fields = [
        "statusBattery1",
        "statusBattery2",
        "statusDoor1",
        "statusDoor2",
        "statusPwr1",
        "statusPwr2",
        "statusFan1",
        "statusFan2",
        "statusHeater1",
        "statusHeater2",
        "statusCamAnpr1",
        "statusCamAnpr2",
        "statusCamAnpr3",
        "statusCamOvr1",
        "statusCamOvr2",
        "statusCamOvr3",
        "statusCamOvr4",
        "statusClw1",
        "statusClw2",
        "statusOverhight1",
        "statusOverhight2",
      ];

      const values = fields
        .map((field) => this.normalizeStatusValue(status[field]))
        .filter((value) => value !== null);

      if (!values.length) {
        return "no-data";
      }

      if (values.some((value) => value === 2)) {
        return "unavailable";
      }

      if (values.some((value) => value === 1)) {
        return "available";
      }

      return "no-data";
    },
    getAvailabilityLabel(status) {
      const state = this.getAvailabilityState(status);
      if (state === "available") {
        return this.$t("available");
      }
      if (state === "unavailable") {
        return this.$t("unavailable");
      }
      return this.$t("no-data");
    },
    getAvailabilityClass(status) {
      const state = this.getAvailabilityState(status);
      if (state === "available") {
        return "status-chip-ok";
      }
      if (state === "unavailable") {
        return "status-chip-bad";
      }
      return "status-chip-na";
    },
    toRows(status) {
      const labelMap = {
        routerIp: "Adres IP routera",
        statusBattery1: "Akumulator 1",
        statusBattery2: "Akumulator 2",
        statusDoor1: "Drzwi 1",
        statusDoor2: "Drzwi 2",
        statusPwr1: "Zasilanie 1",
        statusPwr2: "Zasilanie 2",
        statusFan1: "Wentylator 1",
        statusFan2: "Wentylator 2",
        statusHeater1: "Grzałka 1",
        statusHeater2: "Grzałka 2",
        statusTempIn1: "Temperatura wewnętrzna 1",
        statusTempIn2: "Temperatura wewnętrzna 2",
        statusHumidity1: "Wilgotność 1",
        statusHumidity2: "Wilgotność 2",
        statusUptime: "Czas pracy",
        statusTempOut1: "Temperatura zewnętrzna 1",
        statusTempOut2: "Temperatura zewnętrzna 2",
        statusCamAnpr1: "Kamera ANPR 1",
        statusCamAnpr2: "Kamera ANPR 2",
        statusCamAnpr3: "Kamera ANPR 3",
        statusCamOvr1: "Kamera OVR 1",
        statusCamOvr2: "Kamera OVR 2",
        statusCamOvr3: "Kamera OVR 3",
        statusCamOvr4: "Kamera OVR 4",
        statusClw1: "Czujnik CLW 1",
        statusClw2: "Czujnik CLW 2",
        statusOverhight1: "Czujnik wysokości 1",
        statusOverhight2: "Czujnik wysokości 2",
        statusInternalVoltage: "Napięcie wewnętrzne",
        statusBatteryVoltage: "Napięcie akumulatora",
        statusBatteryCurrent: "Prąd akumulatora",
        statusPwrCurrent: "Prąd zasilania",
        createdAt: "Data zapisu",
      };

      const fields = [
        "routerIp",
        "statusBattery1",
        "statusBattery2",
        "statusDoor1",
        "statusDoor2",
        "statusPwr1",
        "statusPwr2",
        "statusFan1",
        "statusFan2",
        "statusHeater1",
        "statusHeater2",
        "statusTempIn1",
        "statusTempIn2",
        "statusHumidity1",
        "statusHumidity2",
        "statusUptime",
        "statusTempOut1",
        "statusTempOut2",
        "statusCamAnpr1",
        "statusCamAnpr2",
        "statusCamAnpr3",
        "statusCamOvr1",
        "statusCamOvr2",
        "statusCamOvr3",
        "statusCamOvr4",
        "statusClw1",
        "statusClw2",
        "statusOverhight1",
        "statusOverhight2",
        "statusInternalVoltage",
        "statusBatteryVoltage",
        "statusBatteryCurrent",
        "statusPwrCurrent",
        "createdAt",
      ];

      return fields.map((field) => ({
        label: labelMap[field] || field,
        value: field === "statusUptime" || field === "createdAt"
          ? this.formatDateTime(status[field])
          : this.formatValue(status[field]),
      }));
    },
  },
  async mounted() {
    await this.loadLocations();
  },
};
</script>

<style scoped src="../assets/css/viver-style.css"></style>

<style scoped>
.row {
  margin: 0;
}

.content {
  margin-top: 25px;
  background-color: white;
}

.table-container button {
  padding: 5px 40px;
  margin-top: 10px;
  margin-right: 1px;
  background-color: #e0e0e0;
  cursor: pointer;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 100px;
  border: none;
}

h5 {
  margin-top: 13px;
  font-size: 18px !important;
  font-weight: bold;
}

.location-meta {
  font-size: 14px;
  font-weight: normal;
  color: #666;
}

.details-row-field {
  font-family: "Ubuntu", sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
}

.vehicle-panel {
  margin-top: 15px;
}

#road-info .row > div:first-child {
  border: 0;
}

table {
  width: 100%;
}

thead,
tbody {
  width: 100%;
}

.field-col {
  width: 35%;
}

.value-col {
  width: 65%;
}

.status-line {
  padding: 1rem;
  background: #fff;
  border: 1px solid #ececec;
  margin-bottom: 1rem;
}

.status-line.error {
  color: #b00020;
}

.status-section-title {
  margin: 0.5rem 0 0.75rem;
  font-weight: 700;
}

.lp-col {
  width: 80px;
}

.history-table {
  margin-bottom: 1rem;
}

.status-chip {
  display: inline-block;
  min-width: 110px;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-chip-ok {
  background: #e8f5eb;
  color: #1b5e20;
}

.status-chip-bad {
  background: #fdecec;
  color: #a31212;
}

.status-chip-na {
  background: #efefef;
  color: #555;
}

.btn-download:hover {
  background-color: #ccc;
}
</style>
